import type { Category } from '@data-hub/shared';
import { createFileRoute } from '@tanstack/react-router';
import {
  CheckIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AdminGuard } from '@/components/layout/AdminGuard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CategoryFormDialog } from '@/features/categories/CategoryFormDialog';
import { CATEGORY_COLOR_CLASSES } from '@/features/categories/category-colors';
import { useCategories } from '@/features/categories/use-categories';
import { useDeleteCategory } from '@/features/categories/use-category-mutations';
import { handleApiError } from '@/lib/handle-error';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authed/admin/categories')({
  component: AdminCategoriesPage,
});

function AdminCategoriesPage() {
  return (
    <AdminGuard>
      <CategoriesAdmin />
    </AdminGuard>
  );
}

function CategoriesAdmin() {
  const categories = useCategories();
  const del = useDeleteCategory();
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const items = (categories.data?.items ?? []).slice().sort((a, b) => a.order - b.order);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground">Manage resource categories.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon className="size-4" />
          New category
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">Icon</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead className="w-[60px]">Color</TableHead>
              <TableHead className="w-[80px] text-right">Order</TableHead>
              <TableHead className="w-[100px]">Active</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.isPending ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  No categories yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="text-xl">{c.icon}</TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {c.slug}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'inline-block size-4 rounded-full',
                        CATEGORY_COLOR_CLASSES[c.color].dot,
                      )}
                      title={CATEGORY_COLOR_CLASSES[c.color].label}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{c.order}</TableCell>
                  <TableCell>
                    {c.isActive ? (
                      <CheckIcon className="size-4 text-emerald-500" />
                    ) : (
                      <XIcon className="size-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Actions" className="size-8">
                          <MoreHorizontalIcon className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditTarget(c)}>
                          <PencilIcon className="size-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem variant="destructive" onClick={() => setDeleteTarget(c)}>
                          <TrashIcon className="size-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CategoryFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CategoryFormDialog
        key={editTarget?.id ?? 'edit'}
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
        category={editTarget}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate <strong>{deleteTarget?.name}</strong>. Resources tagged with it
              will keep their reference but the category will be hidden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                if (!deleteTarget) return;
                del.mutate(deleteTarget.id, {
                  onSuccess: () => {
                    toast.success('Category deleted');
                    setDeleteTarget(null);
                  },
                  onError: (err) => handleApiError(err, 'Failed to delete category'),
                });
              }}
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
